/**
 * OpenReplay Session Replay and Analytics Configuration
 *
 * This module initializes the OpenReplay tracker for session recording
 * and user analytics. Privacy settings are configured to obscure sensitive
 * data like emails while allowing sleep-related data for debugging.
 */

import Tracker from '@openreplay/tracker';

// Only initialize if OpenReplay is enabled via environment variable
const isEnabled = process.env.REACT_APP_OPENREPLAY_ENABLED === 'true';
const projectKey = process.env.REACT_APP_OPENREPLAY_KEY;
const ingestPoint = process.env.REACT_APP_OPENREPLAY_INGEST;

// DEBUG: Log configuration on module load
console.log('[OpenReplay Debug] Configuration:', {
  isEnabled,
  hasProjectKey: !!projectKey,
  projectKeyPrefix: projectKey ? projectKey.substring(0, 15) + '...' : 'MISSING',
  hasIngestPoint: !!ingestPoint,
  ingestPoint: ingestPoint || 'MISSING',
  nodeEnv: process.env.NODE_ENV,
  allEnvKeys: Object.keys(process.env).filter(k => k.startsWith('REACT_APP_OPENREPLAY'))
});

let tracker: Tracker | null = null;
let isTracking = false; // Flag to prevent double-starting

if (isEnabled && projectKey && ingestPoint) {
  try {
    tracker = new Tracker({
      projectKey,
      ingestPoint,

      // Privacy settings - Standard privacy level
      // Obscure emails but allow numbers (important for sleep time debugging)
      obscureTextEmails: true,        // Hide emails in text content
      obscureTextNumbers: false,      // Allow numbers (sleep times, durations)
      obscureInputEmails: true,       // Hide email input fields
      obscureInputNumbers: false,     // Allow number inputs (sleep data)

      // Network recording (optional - captures API calls for debugging)
      // Set to false if you don't want to record API requests
      __DISABLE_SECURE_MODE: process.env.NODE_ENV === 'development',
    });

    console.log('[OpenReplay] Tracker initialized successfully', tracker);
  } catch (error) {
    console.error('[OpenReplay] Failed to initialize tracker:', error);
  }
} else {
  console.warn('[OpenReplay] Tracker disabled or not configured:', {
    isEnabled,
    hasKey: !!projectKey,
    hasIngest: !!ingestPoint
  });
}

/**
 * Start the OpenReplay session recording
 * Call this after user authentication or app initialization
 * Safe to call multiple times - will only start once
 */
export const startTracking = (): void => {
  console.log('[OpenReplay Debug] startTracking() called, tracker exists:', !!tracker, 'already tracking:', isTracking);

  if (!tracker) {
    console.warn('[OpenReplay] Cannot start tracking - tracker not initialized');
    return;
  }

  if (isTracking) {
    console.log('[OpenReplay] Tracking already started, skipping');
    return;
  }

  try {
    tracker.start();
    isTracking = true;
    console.log('[OpenReplay] Session tracking started');
  } catch (error) {
    console.error('[OpenReplay] Failed to start tracking:', error);
  }
};

/**
 * Stop the OpenReplay session recording
 * Call this on logout or when user opts out
 */
export const stopTracking = (): void => {
  if (tracker && isTracking) {
    tracker.stop();
    isTracking = false;
    console.log('[OpenReplay] Session tracking stopped');
  }
};

/**
 * Set user identification for session tracking
 * @param userId - Unique user identifier
 * @param metadata - Additional user metadata (optional)
 */
export const identifyUser = (userId: string, metadata?: Record<string, any>): void => {
  if (tracker) {
    tracker.setUserID(userId);

    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        tracker!.setMetadata(key, value);
      });
    }

    console.log('[OpenReplay] User identified:', userId);
  }
};

/**
 * Track a custom event with optional payload
 * @param eventName - Name of the event
 * @param payload - Event data (optional)
 */
export const trackEvent = (eventName: string, payload?: Record<string, any>): void => {
  if (tracker) {
    tracker.event(eventName, payload);
    console.log('[OpenReplay] Event tracked:', eventName, payload);
  }
};

/**
 * Track page navigation
 * @param pageName - Name of the page/route
 */
export const trackPageView = (pageName: string): void => {
  if (tracker) {
    tracker.event('page_view', { page: pageName });
  }
};

export default tracker;
